import React from "react";
import PropTypes from "prop-types";
import { Modal, Button, Alert, Table, Spinner } from "react-bootstrap";
import moment from "moment";

import { flattenAddress } from "../../../../utils/address";

/**
 * Modal de confirmation pour l'extraction d'une commande Uber annulee.
 * Affiche les details de la commande et demande confirmation avant l'extraction.
 *
 * @param {Object} props.modal - Donnees de la modal depuis Redux
 * @param {Object} props.modal.task - La tache a extraire
 * @param {Object} props.modal.dock - Le dock contenant la tache
 * @param {Function} props.onHide - Callback pour fermer la modal
 * @param {Function} props.onConfirm - Callback pour confirmer l'extraction
 */
const UberCancelResolutionModal = ({ modal, onHide, onConfirm }) => {
  // Verifier si la modal doit s'afficher
  if (!modal || modal.modalType !== "uber_cancel_resolution") {
    return null;
  }

  const { task, dock, sending } = modal;

  if (!task || !dock) {
    return null;
  }

  // Recuperer les donnees de la tache
  const order = task.linked_order;
  const address = task.address;
  const externalJob = task.external_shipping_jobs && task.external_shipping_jobs[0];
  const isAlreadyCanceled = !externalJob || externalJob.status === "canceled";
  const cancelReason = externalJob && externalJob.cancel_reason;
  const resolveError = task.uberResolveError;

  // Formater le creneau de livraison
  const formatSlot = () => {
    if (!order) {
      return "—";
    }
    const start = order.shipping_time
      ? moment(order.shipping_time).format("HH:mm")
      : null;
    const end = order.max_shipping_time
      ? moment(order.max_shipping_time).format("HH:mm")
      : null;
    if (start && end) {
      return `${start} - ${end}`;
    }
    return start || end || "—";
  };

  // Traduire le statut
  const translateStatus = (status) => {
    const translations = {
      "canceled": "Annulée",
      "pending": "En attente",
      "in_progress": "En cours",
      "completed": "Complete",
    };
    return translations[status] || status;
  };

  // Transmettre le delivery_id pour cibler explicitement l'ESJ courant
  const deliveryId = externalJob && externalJob.delivery_id
    ? externalJob.delivery_id
    : null;

  const handleConfirm = () => {
    onConfirm(task, dock, deliveryId);
  };

  return (
    <Modal
      show={modal.modalType === "uber_cancel_resolution"}
      onHide={onHide}
      animation={false}
      backdrop
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isAlreadyCanceled
            ? "Extraction de commande Uber annulee"
            : "Annuler et extraire la commande Uber"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Affichage des erreurs */}
        {resolveError && (
          <Alert variant="danger" className="mb-3">
            <Alert.Heading>Erreur</Alert.Heading>
            <p>{resolveError}</p>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleConfirm}
              disabled={sending}
            >
              Reessayer
            </Button>
          </Alert>
        )}

        {/* Description de l'action */}
        <Alert variant="warning" className="mb-3">
          <i className="fa-solid fa-triangle-exclamation me-2"></i>
          {isAlreadyCanceled ? (
            <>
              <span>
                Cette livraison Uber a ete annulee. Voulez-vous l&apos;extraire du dock ?
              </span>
              <br />
              <small>
                La commande sera deplacee vers un nouveau groupement.
              </small>
            </>
          ) : (
            <span>
              Vous allez annuler la livraison Uber pour cette commande et l&apos;extraire du dock vers un nouveau groupement.
            </span>
          )}
        </Alert>

        {/* Details de la commande */}
        <Table striped bordered hover size="sm" className="mb-3">
          <tbody>
            <tr>
              <td className="font-weight-bold" style={{ width: "150px" }}>Commande</td>
              <td>
                <strong>#{order ? order.random_id : "—"}</strong>
                {order && (
                  <small className="text-muted ms-2">(ID: {order.id})</small>
                )}
              </td>
            </tr>
            <tr>
              <td className="font-weight-bold">Adresse</td>
              <td>
                <small>{address ? flattenAddress(address) : "—"}</small>
              </td>
            </tr>
            <tr>
              <td className="font-weight-bold">Creneau</td>
              <td>{formatSlot()}</td>
            </tr>
            <tr>
              <td className="font-weight-bold">Statut Uber</td>
              <td>
                {externalJob ? (
                  <span className={isAlreadyCanceled ? "badge badge-danger" : "badge badge-warning"}>
                    {translateStatus(externalJob.status)}
                  </span>
                ) : "—"}
              </td>
            </tr>
            {cancelReason && (
              <tr>
                <td className="font-weight-bold">Raison annulation</td>
                <td>
                  <small className="text-danger">{cancelReason}</small>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          size="sm"
          onClick={onHide}
          disabled={sending}
        >
          Annuler
        </Button>
        <Button
          variant={isAlreadyCanceled ? "danger" : "warning"}
          size="sm"
          onClick={handleConfirm}
          disabled={sending}
        >
          {sending ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Extraction en cours...
            </>
          ) : isAlreadyCanceled ? (
            <>
              <i className="fa-solid fa-eject me-1"></i>
              Extraire du dock
            </>
          ) : (
            <>
              <i className="fa-solid fa-ban me-1"></i>
              Annuler et extraire
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

UberCancelResolutionModal.propTypes = {
  modal: PropTypes.object,
  onHide: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default UberCancelResolutionModal;
